import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import {
  Filter,
  Building2,
  Store,
  ChevronDown,
  FileSpreadsheet,
  Users,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { getPartnersList, getMerchantReport } from '../../services/reports';
import ReportDateRangeEnforcer from '../../components/ReportDateRangeEnforcer';
import { useAppSelector } from '../../store/hooks/reduxHooks';
import { UserRole } from '../../types/auth';

// Types
interface MerchantDataFilters {
  dateRange: {
    from: string | null;
    to: string | null;
  };
  brands: string[];
  lender: string;
  reportType: string;
  bankDetails?: boolean;
  GST?: boolean;
}

interface SelectOption {
  value: string;
  label: string;
}

const MerchantDataReportPage: React.FC = () => {
  // Filter state
  const [filters, setFilters] = useState<MerchantDataFilters>({
    dateRange: {
      from: null,
      to: null
    },
    brands: [],
    lender: '',
    reportType: 'basic',
    bankDetails: false,
    GST: false
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat] = useState<'excel'>('excel');

  // Available options for filters
  const [brandOptions, setBrandOptions] = useState<SelectOption[]>([]);
  const [lenderOptions, setLenderOptions] = useState<SelectOption[]>([]);
  const [reportTypeOptions] = useState<SelectOption[]>([
    { value: 'basic', label: 'Basic Report' },
    { value: 'extended', label: 'Extended Report' }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMerchantLoading, setIsMerchantLoading] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const { user } = useAppSelector(state => state.auth);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const partners = await getPartnersList();
        let brandOpts = partners.map(name => ({ value: name, label: name }));

        // Filter partners for CleverPe users based on their groups
        if (user?.role === UserRole.CLEVERPE_USER && user?.groups && user.groups.length > 0) {
          brandOpts = brandOpts.filter(option => user.groups?.includes(option.value));
        }

        setBrandOptions(brandOpts);

        // Use static lender options since they don't change often
        const staticLenders = ['cashe', 'zype'];
        const lenderOpts = staticLenders.map(name => ({ value: name, label: name }));
        setLenderOptions(lenderOpts);
      } catch (error) {
        console.error('Error fetching options:', error);
        setBrandOptions([]);
        setLenderOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user]);

  useEffect(() => {
    // Check if filter is applied
    const hasDateRange = filters.dateRange.from && filters.dateRange.to;
    const hasOtherFilters = filters.brands.length > 0 || filters.lender;

    setIsFilterApplied(!!hasDateRange && !!hasOtherFilters);
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key: keyof MerchantDataFilters, value: any) => {
    if (key === 'dateRange') {
      setFilters(prev => ({
        ...prev,
        dateRange: {
          ...prev.dateRange,
          ...value
        }
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setFilters({
      dateRange: {
        from: null,
        to: null
      },
      brands: [],
      lender: '',
      reportType: 'basic',
      bankDetails: false,
      GST: false
    });
  };

  // Update export handler
  const handleExportData = async (format: 'excel') => {
    if (format !== 'excel') return;

    setIsExporting(true);
    setError(null);

    try {
      if (filters.dateRange.from && filters.dateRange.to) {
        const fromDate = new Date(filters.dateRange.from);
        const toDate = new Date(filters.dateRange.to);
        if (fromDate > toDate) {
          setError('From date cannot be later than To date');
          return;
        }
      }

      const params: {
        partner?: string | string[];
        lender?: string;
        from?: string;
        to?: string;
        reportType: string;
        bankDetails?: boolean;
        GST?: boolean;
      } = {
        reportType: filters.reportType
      };

      // Add optional parameters only if they have values
      if (filters.lender) params.lender = filters.lender;
      if (filters.dateRange.from) params.from = filters.dateRange.from;
      if (filters.dateRange.to) params.to = filters.dateRange.to;
      if (filters.bankDetails) params.bankDetails = filters.bankDetails;
      if (filters.GST) params.GST = filters.GST;

      // Handle multiple partners
      if (filters.brands.length > 0) {
        params.partner = filters.brands;
      }

      // CleverPe User validation
      if (user?.role === UserRole.CLEVERPE_USER) {
        const hasDateRange = filters.dateRange.from && filters.dateRange.to;
        const hasOtherFilters = filters.brands.length > 0 || filters.lender;

        if (!hasDateRange) {
          setError('Date range is required for CleverPe users');
          return;
        }

        if (!hasOtherFilters) {
          setError('At least one additional filter is required for CleverPe users');
          return;
        }
      } else if (!filters.brands.length && !params.lender && !params.from && !params.to) {
        setError('Please select at least one filter');
        return;
      }

      const blob = await getMerchantReport(params);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `merchant-data-${filters.reportType}-${new Date().toISOString()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      console.error('Error exporting data:', error);
      setError(error?.message || 'Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={`w-full space-y-6 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
      {/* Error Message */}
      {error && (
        <div className="p-4 duration-300 border-l-4 border-red-400 rounded-md bg-red-50 animate-in fade-in slide-in-from-top">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-red-400" />
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-500"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Filter controls */}
      <div className="flex flex-col items-start justify-between space-y-4 sm:flex-row sm:items-center sm:space-y-0">
        <div>
          {/* Header title removed - handled by Header component */}
        </div>

        <div className="flex space-x-3">
          <Button
            isLoading={isExporting && exportFormat === 'excel'}
            onClick={() => handleExportData('excel')}
            className="flex items-center"
          >
            <FileSpreadsheet size={16} className="mr-1.5" />
            {isExporting && exportFormat === 'excel' ? 'Downloading...' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {/* Filter panel - Always visible */}
      <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        <h2 className="mb-4 text-lg text-gray-800 font-gilroy-semibold">Merchant Data Report Filters</h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {/* Date range filters */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Registration Date Range</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block mb-1 text-xs text-gray-500">From</label>
                <input
                  type="date"
                  value={filters.dateRange.from || ''}
                  onChange={(e) => handleFilterChange('dateRange', { from: e.target.value || null })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-500">To</label>
                <input
                  type="date"
                  value={filters.dateRange.to || ''}
                  onChange={(e) => handleFilterChange('dateRange', { to: e.target.value || null })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Updated Brand filter to support multiple selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Partner/Brand</label>
            <Select
              isMulti
              isClearable
              isSearchable
              isLoading={isLoading}
              value={brandOptions.filter(opt => filters.brands.includes(opt.value))}
              onChange={(selected) => handleFilterChange('brands', selected ? selected.map(item => item.value) : [])}
              options={brandOptions}
              className="text-sm"
              placeholder="Search partners/brands..."
              noOptionsMessage={() => "No partners found"}
            />
          </div>

          {/* Lender filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Lender</label>
            <Select
              isClearable
              isSearchable
              isLoading={isLoading}
              value={lenderOptions.find(opt => opt.value === filters.lender)}
              onChange={(selected) => handleFilterChange('lender', selected?.value || '')}
              options={lenderOptions}
              className="text-sm"
              placeholder="Search lenders..."
              noOptionsMessage={() => "No lenders found"}
            />
          </div>

          {/* Report Type filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Report Type</label>
            <Select
              isClearable={false}
              value={reportTypeOptions.find(opt => opt.value === filters.reportType)}
              onChange={(selected) => handleFilterChange('reportType', selected?.value || 'basic')}
              options={reportTypeOptions}
              className="text-sm"
              placeholder="Select report type"
            />
          </div>

          <div className="flex flex-col space-y-3 mt-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.bankDetails}
                onChange={(e) => handleFilterChange('bankDetails', e.target.checked)}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-sm text-gray-700">Include Merchant Bank Details</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.GST}
                onChange={(e) => handleFilterChange('GST', e.target.checked)}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-sm text-gray-700">Include Merchant GST No</span>
            </label>
          </div>

        </div>

        {/* Filter actions */}
        <div className="flex justify-end mt-6 space-x-3">
          <Button
            variant="outline"
            onClick={handleResetFilters}
            size="sm"
          >
            Reset Filters
          </Button>
          <Button
            onClick={() => handleExportData('excel')}
            size="sm"
          >
            Apply Filters
          </Button>
        </div>
      </div>

      {/* Wrap the export section with ReportDateRangeEnforcer */}
      <ReportDateRangeEnforcer
        dateRange={{
          startDate: filters.dateRange.from ? new Date(filters.dateRange.from) : null,
          endDate: filters.dateRange.to ? new Date(filters.dateRange.to) : null
        }}
        isFilterApplied={isFilterApplied}
      >
        {/* Export section */}
        <div className="p-6 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex flex-col items-center justify-between md:flex-row">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg text-gray-800 font-gilroy-bold">Export Merchant Data</h3>
              <p className="mt-1 text-gray-600">
                {isExporting ? 'Preparing your download...' :
                  error ? 'Please check the filters and try again' :
                    `Download the ${filters.reportType} merchant report with all applied filters`}
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                isLoading={isExporting}
                onClick={() => handleExportData('excel')}
                disabled={isLoading || Boolean(error)}
                className="flex items-center"
              >
                <FileSpreadsheet size={16} className="mr-1.5" />
                Excel ({filters.reportType})
              </Button>
            </div>
          </div>
        </div>
      </ReportDateRangeEnforcer>
    </div>
  );
};

export default MerchantDataReportPage;